import { useState, useEffect } from 'react';
import { useAppNavigate, usePwaNav } from '../contexts/PwaNavContext';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { stableEmoji } from '../types/passport';
import PassportCover from './passport/PassportCover';
import { ChevronLeft, Swords, Bell, BellOff, UserX } from 'lucide-react';
import './FoodPassportPanel.css';
import './FriendProfileModal.css';

interface FriendCard {
  id: string;
  displayName: string;
  photoURL: string | null;
  level: string;
  totalPoints: number;
  totalOrders: number;
  stamps: { id: string; name: string }[];
}

interface FriendPassport {
  customer: { displayName: string; totalOrders: number; loyaltyPoints: number; level: string };
  restaurants: { id: string; name: string; orders: number; lastOrder: string }[];
  achievements: { id: string; name: string; description: string; icon: string | null; points: number; unlockedAt: string }[];
  badges: { id: string; name: string; icon: string | null; rarity: string; unlockedAt: string }[];
}

export default function FriendProfileModal() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { navData } = usePwaNav();
  const { token } = useCustomerAuth();

  const friendId = navData['friendId'] ?? new URLSearchParams(window.location.search).get('friendId') ?? '';

  const [friend, setFriend] = useState<FriendCard | null>(null);
  const [friendshipId, setFriendshipId] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [muteLoading, setMuteLoading] = useState(false);
  const [passport, setPassport] = useState<FriendPassport | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    if (!friendId || !token) {
      if (!token) setLoading(true); // keep loading while token resolves
      else setLoading(false);
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get<{ friends: FriendCard[]; incoming: unknown[]; outgoing: unknown[] }>(
        `${API_URL || ''}/api/friends`,
        { headers }
      ),
      axios.get<{ muted: boolean }>(
        `${API_URL || ''}/api/gifts/mute-status/${friendId}`,
        { headers }
      ),
      axios.get<FriendPassport>(
        `${API_URL || ''}/api/friends/${friendId}/passport`,
        { headers }
      ).catch(() => ({ data: null as FriendPassport | null })),
    ])
      .then(([friendsRes, muteRes, passportRes]) => {
        const found = friendsRes.data.friends.find((f: any) => f.id === friendId) ?? null;
        setFriend(found);
        setFriendshipId((found as any)?.friendshipId ?? null);
        setMuted(muteRes.data.muted);
        setPassport(passportRes.data);
      })
      .catch((err) => {
        console.error('[FriendProfile] fetch failed:', err?.response?.data || err.message);
      })
      .finally(() => setLoading(false));
  }, [friendId, token]);

  const handleToggleMute = async () => {
    if (!token || muteLoading) return;
    setMuteLoading(true);
    try {
      await axios.post(
        `${API_URL || ''}/api/gifts/mute`,
        { friendId, mute: !muted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMuted((prev) => !prev);
    } catch {
      // silently ignore
    } finally {
      setMuteLoading(false);
    }
  };

  return (
    <div className="fpm-page">
      <div className="fpm-header">
        <button className="fpm-back-btn" onClick={() => navigate('/')} aria-label={t('common.back', 'Back')}>
          <ChevronLeft size={20} />
        </button>
        <div className="fpm-header-title">{t('friends.profile', 'Profile')}</div>
        {/* spacer to keep title centred */}
        <div style={{ width: 36 }} />
      </div>

      {loading ? (
        <div className="fpm-loading">{t('common.loading', 'Loading…')}</div>
      ) : !friend ? (
        <div className="fpm-loading">{t('friends.notFound', 'Friend not found')}</div>
      ) : (
        <div className="fpm-content">
          {/* Friend's passport cover (flippable) */}
          {passport ? (() => {
            const bestRestaurantName = passport.restaurants.length > 0
              ? passport.restaurants.reduce((best, r) => r.orders > best.orders ? r : best, passport.restaurants[0]).name
              : null;
            return (
              <PassportCover
                holderName={passport.customer.displayName}
                totalOrders={passport.customer.totalOrders}
                totalPoints={passport.customer.loyaltyPoints}
                restaurants={passport.restaurants}
                isFlipped={isFlipped}
                onFlip={() => setIsFlipped(f => !f)}
                bestRestaurantName={bestRestaurantName}
                favoriteDishName={null}
              />
            );
          })() : (
            <>
              <div className="fpm-avatar">
                {friend.photoURL
                  ? <img src={friend.photoURL} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : friend.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="fpm-name">{friend.displayName}</div>
              <div className="fpm-level">{friend.level}</div>
            </>
          )}

          {/* Stamps — premium look matching own passport */}
          {passport && passport.restaurants.length > 0 && (
            <>
              <div className="pp-section-header"><span className="pp-section-title">{t('foodPassport.stamps')}</span></div>
              <div className="pp-stamps-row">
                {passport.restaurants.map((r, i) => {
                  const STAMP_COLORS = ['#FF6B35','#4ECDC4','#FFD93D','#A8E6CF','#FF8B94','#C7B8EA'];
                  const color = STAMP_COLORS[i % STAMP_COLORS.length];
                  return (
                    <div
                      key={r.id}
                      className="pp-rstamp"
                      style={{ ['--rs-tint' as string]: color } as React.CSSProperties}
                    >
                      <div className="pp-rstamp-tint" aria-hidden />
                      <span className="pp-rstamp-emoji">{stableEmoji(r.id)}</span>
                      <span className="pp-rstamp-name">{r.name.toUpperCase().slice(0, 10)}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Unified rewards grid (badges + unlocked) */}
          {passport && (passport.badges.length + passport.achievements.length) > 0 && (
            <div className="pp-rewards-grid">
              {passport.badges.map((b) => (
                <div key={`b-${b.id}`} className="pp-reward pp-reward--badge" style={{ cursor: 'default' }}>
                  <div className="pp-reward-icon">{b.icon || '🏅'}</div>
                  <div className="pp-reward-name">{b.name}</div>
                  <div className="pp-reward-pin" aria-hidden>✓</div>
                </div>
              ))}
              {passport.achievements.map((a) => (
                <div key={`a-${a.id}`} className="pp-reward pp-reward--unlocked" style={{ cursor: 'default' }}>
                  <div className="pp-reward-icon">{a.icon || '⭐'}</div>
                  <div className="pp-reward-name">{a.name}</div>
                  <div className="pp-reward-pin" aria-hidden>✓</div>
                </div>
              ))}
            </div>
          )}

          <div className="fpm-actions">
            <button className="fpm-action fpm-action--disabled" disabled>
              <Swords size={18} />
              <span>{t('challenges.challenge', 'Вызвать на челлендж')}</span>
            </button>

            <button className="fpm-action" onClick={handleToggleMute} disabled={muteLoading}>
              {muted ? <BellOff size={18} /> : <Bell size={18} />}
              <span>
                {muted
                  ? t('friends.unmuteNotifications', 'Включить уведомления')
                  : t('friends.muteNotifications', 'Отключить уведомления')}
              </span>
            </button>

            {friendshipId && (
              <button
                className="fpm-action fpm-action--danger"
                onClick={async () => {
                  if (!token) return;
                  try {
                    await axios.delete(`${API_URL || ''}/api/friends/${friendshipId}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    navigate('/');
                  } catch { /* */ }
                }}
              >
                <UserX size={18} />
                <span>{t('friends.unfriend', 'Unfriend')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
